import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SCRIPT_FIELD_TYPES, checkScript,
  type ScriptBody, type ScriptDetail, type ScriptField, type ScriptFieldType, type ScriptStep,
} from '@arcnet/contracts';
import { useDeleteScript, useDiallerLists, useDiallerRecords, useSaveScript, useScript, useScripts } from '../../lib/api/hooks-dialler.js';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useSubroute } from '../../lib/subroute.js';
import { relativeTime } from '../../lib/format.js';
import { Empty, ErrorBanner, SectionHead, SubHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { ScriptText } from './ScriptText.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Call scripts (docs/36 §8.3, 0129): what agents say on a preview campaign's calls, filled in from
 * the lead. Opened by name: /dialler/call-scripts/renewals.
 *
 * Every save that changes a script is a new version; calls keep the version they were shown.
 */

const slugOf = (name: string): string =>
  name.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'script';
const keyOf = (label: string, taken: ReadonlySet<string>, fallback: string): string => {
  const base = (label.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '') || fallback).slice(0, 36);
  let key = base; let n = 2;
  while (taken.has(key)) key = `${base}_${n++}`;
  return key;
};

const TYPE_LABEL: Record<ScriptFieldType, string> = { text: 'Text', number: 'Number', date: 'Date', choice: 'Choice', yes_no: 'Yes / no' };

const STARTER: ScriptBody = {
  steps: [
    { key: 'greeting', title: 'Greeting', text: 'Hi {{first_name|there}}, this is {{agent.first_name}} calling from …', buttons: [], fields: [] },
  ],
};

export function ScriptsPage({ tenantId }: { tenantId: string }) {
  const route = useSubroute();
  const can = useSession((s) => s.can);
  const scripts = useScripts(tenantId);
  const all = scripts.data?.data ?? [];
  const open = route.rest[0];

  if (open === 'new') {
    return (
      <>
        <Crumb onBack={() => route.go([])} />
        <Editor tenantId={tenantId} detail={null} onSaved={(name) => route.go([slugOf(name)], { replace: true })} onGone={() => route.go([])} />
      </>
    );
  }
  if (open) {
    const s = all.find((x) => slugOf(x.name) === open) ?? null;
    return (
      <>
        <Crumb onBack={() => route.go([])} />
        {scripts.data && !s && (
          <div className="arc-banner arc-banner-warn"><Icon name="warning" size={14} /><span>There is no script called {open}.</span></div>
        )}
        {s && <Loaded tenantId={tenantId} id={s.id} onSaved={(name) => { if (slugOf(name) !== open) route.go([slugOf(name)], { replace: true }); }} onGone={() => route.go([])} />}
      </>
    );
  }

  return (
    <>
      <SectionHead title="Call scripts" sub="What agents say on a preview campaign’s calls, filled in from the lead."
        action={can('campaign:write')
          ? <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => route.go(['new'])}>
              <Icon name="plus" size={13} /> New script
            </button>
          : undefined} />
      {scripts.error && <ErrorBanner error={scripts.error} />}
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead><tr><th>Script</th><th className="arc-td-right">Steps</th><th className="arc-td-right">Campaigns</th><th>Version</th><th>Changed</th></tr></thead>
            {scripts.data === undefined ? <TableSkeleton query={scripts} cols={5} /> : (
              <tbody>
                {all.map((s) => (
                  <tr key={s.id} className="arc-clickable" onClick={() => route.go([slugOf(s.name)])}>
                    <td style={{ fontWeight: 570 }}>{s.name}</td>
                    <td className="arc-td-right arc-num">{s.steps}</td>
                    <td className="arc-td-right arc-num">{s.campaigns}</td>
                    <td className="arc-num">{s.version}</td>
                    <td className="arc-num">{relativeTime(s.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {scripts.data && all.length === 0 && <Empty icon="media" title="No scripts" hint="A script is shown to agents beside each call, with the lead’s details filled in." />}
      </div>
    </>
  );
}

function Crumb({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" className="arc-crumb" onClick={onBack} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
      <Icon name="arrowLeft" size={15} /> Call scripts
    </button>
  );
}

function Loaded({ tenantId, id, onSaved, onGone }: { tenantId: string; id: string; onSaved: (name: string) => void; onGone: () => void }) {
  const q = useScript(tenantId, id);
  if (!q.data) return q.error ? <ErrorBanner error={q.error} /> : <TableSkeleton rows={3} cols={3} />;
  return <Editor key={`${id}:${q.data.version}`} tenantId={tenantId} detail={q.data} onSaved={onSaved} onGone={onGone} />;
}

// ================================================================================== the editor

function Editor({ tenantId, detail, onSaved, onGone }: {
  tenantId: string; detail: ScriptDetail | null; onSaved: (name: string) => void; onGone: () => void;
}) {
  const can = useSession((s) => s.can);
  const writer = can('campaign:write');
  const save = useSaveScript(tenantId);
  const del = useDeleteScript(tenantId);
  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: Array<{ code: string; label: string; is_active: boolean }> }>(`/v1/tenants/${tenantId}/disposition-codes`),
    enabled: !!tenantId,
  });
  const active = (codes.data?.data ?? []).filter((c) => c.is_active);

  const [name, setName] = useState(detail?.name ?? '');
  const [body, setBody] = useState<ScriptBody>(() => detail?.body ?? STARTER);
  const [at, setAt] = useState(0);
  useEffect(() => { save.reset(); }, [name, body]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = body.steps[Math.min(at, body.steps.length - 1)]!;
  const setStep = (patch: Partial<ScriptStep>) =>
    setBody((b) => ({ steps: b.steps.map((s, i) => (i === at ? { ...s, ...patch } : s)) }));

  // The problems as they stand now, against the lists' fields and the account's dispositions.
  const listKeys = useMemo(() => (detail?.available ?? []).filter((v) => v.name.startsWith('custom.')).map((v) => v.name.slice(7)), [detail]);
  const problems = codes.data ? checkScript(body, listKeys, active.map((c) => c.code)) : [];
  const variables = [
    ...(detail?.available ?? []),
    ...body.steps.flatMap((s) => s.fields).filter((f) => !listKeys.includes(f.key)).map((f) => ({ name: `custom.${f.key}`, label: `${f.label} (captured)` })),
  ];

  const text = useRef<HTMLTextAreaElement>(null);
  const insert = (v: string) => {
    const el = text.current;
    const token = `{{${v}}}`;
    if (!el) { setStep({ text: step.text + token }); return; }
    const [a, b] = [el.selectionStart, el.selectionEnd];
    setStep({ text: step.text.slice(0, a) + token + step.text.slice(b) });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + token.length, a + token.length); });
  };

  const addStep = () => {
    const key = keyOf(`step ${body.steps.length + 1}`, new Set(body.steps.map((s) => s.key)), 'step');
    setBody((b) => ({ steps: [...b.steps, { key, title: `Step ${b.steps.length + 1}`, text: '', buttons: [], fields: [] }] }));
    setAt(body.steps.length);
  };
  const move = (d: -1 | 1) => {
    const j = at + d;
    if (j < 0 || j >= body.steps.length) return;
    setBody((b) => { const s = [...b.steps]; [s[at], s[j]] = [s[j]!, s[at]!]; return { steps: s }; });
    setAt(j);
  };
  const removeStep = () => {
    if (body.steps.length === 1) return;
    const gone = step.key;
    setBody((b) => ({
      steps: b.steps.filter((_, i) => i !== at)
        .map((s) => ({ ...s, buttons: s.buttons.map((btn) => (btn.go_to === gone ? { ...btn, go_to: null } : btn)) })),
    }));
    setAt(Math.max(0, at - 1));
  };
  const allFieldKeys = new Set(body.steps.flatMap((s) => s.fields.map((f) => f.key)));
  const setField = (j: number, patch: Partial<ScriptField>) => setStep({ fields: step.fields.map((f, k) => (k === j ? { ...f, ...patch } : f)) });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({ ...(detail ? { id: detail.id } : {}), name: name.trim(), body }, { onSuccess: () => onSaved(name.trim()) });
  };

  return (
    <form onSubmit={submit}>
      <SectionHead title={detail ? detail.name : 'New script'}
        sub={detail ? `Version ${detail.version} · used by ${detail.campaigns} campaign${detail.campaigns === 1 ? '' : 's'}` : undefined}
        action={writer && detail ? (
          <RowAction kind="delete" label={`Remove ${detail.name}`} disabled={del.isPending}
            onClick={() => { void confirmAction({ title: `Remove ${detail.name}?`, body: 'Calls already made keep the version they were shown.', action: 'Remove', danger: true }).then((ok) => { if (ok) del.mutate(detail.id, { onSuccess: onGone }); }); }} />
        ) : undefined} />
      {del.error && <ErrorBanner error={del.error} />}

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0, maxWidth: 360 }}>
          <label className="arc-label" htmlFor="sc-name">Name</label>
          <input id="sc-name" className="arc-input" value={name} maxLength={120} required disabled={!writer}
            onChange={(e) => setName(e.target.value)} placeholder="Renewals" />
        </div>
      </div>

      {problems.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>Campaigns using this script won’t start until these are fixed: {problems.join('; ')}.</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 220px) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        {/* The steps, in order. */}
        <div className="arc-card">
          <ol style={{ listStyle: 'none', margin: 0, padding: 6 }}>
            {body.steps.map((s, i) => (
              <li key={s.key}>
                <button type="button" className={`arc-btn arc-btn-sm ${i === at ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 2 }} onClick={() => setAt(i)}>
                  <span className="arc-num" style={{ opacity: 0.6, marginRight: 6 }}>{i + 1}</span>{s.title}
                </button>
              </li>
            ))}
          </ol>
          {writer && (
            <div className="arc-row" style={{ gap: 4, padding: '0 6px 8px', flexWrap: 'wrap' }}>
              <button type="button" className="arc-btn arc-btn-sm" onClick={addStep} disabled={body.steps.length >= 30}><Icon name="plus" size={12} /> Step</button>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Move up" onClick={() => move(-1)} disabled={at === 0}><Icon name="chevronUp" size={12} /></button>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Move down" onClick={() => move(1)} disabled={at === body.steps.length - 1}><Icon name="chevronDown" size={12} /></button>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <div className="arc-card arc-card-body">
            <div className="arc-row" style={{ gap: 10, alignItems: 'flex-end' }}>
              <div className="arc-field" style={{ margin: 0, flex: 1, maxWidth: 360 }}>
                <label className="arc-label" htmlFor="sc-title">Step</label>
                <input id="sc-title" className="arc-input" value={step.title} maxLength={60} disabled={!writer} onChange={(e) => setStep({ title: e.target.value })} />
              </div>
              {writer && body.steps.length > 1 && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={removeStep}>Remove step</button>
              )}
            </div>
            <div className="arc-field" style={{ margin: '12px 0 0' }}>
              <label className="arc-label" htmlFor="sc-text">What the agent says</label>
              <textarea id="sc-text" ref={text} className="arc-input" rows={7} maxLength={4000} value={step.text} disabled={!writer}
                onChange={(e) => setStep({ text: e.target.value })} style={{ fontFamily: 'inherit' }} />
              <div className="arc-hint">**bold**, a line starting with “- ” for a list, {'{{first_name|there}}'} for a value with a fallback.</div>
            </div>
            {writer && (
              <div className="arc-row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 8 }} aria-label="Insert a value">
                {variables.map((v) => (
                  <button key={v.name} type="button" className="arc-btn arc-btn-ghost arc-btn-sm" title={`{{${v.name}}}`} onClick={() => insert(v.name)}>{v.label}</button>
                ))}
              </div>
            )}
          </div>

          <div className="arc-card arc-card-body">
            <SubHead sub="Move to another step, and optionally choose the call’s disposition.">Buttons</SubHead>
            {step.buttons.length > 0 && (
              <table className="arc-table">
                <thead><tr><th>Label</th><th>Goes to</th><th>Disposition</th><th /></tr></thead>
                <tbody>
                  {step.buttons.map((b, j) => (
                    <tr key={j}>
                      <td><input className="arc-input" aria-label="Button label" value={b.label} maxLength={40} disabled={!writer}
                        onChange={(e) => setStep({ buttons: step.buttons.map((x, k) => (k === j ? { ...x, label: e.target.value } : x)) })} /></td>
                      <td><select className="arc-select" aria-label="Goes to" value={b.go_to ?? ''} disabled={!writer}
                        onChange={(e) => setStep({ buttons: step.buttons.map((x, k) => (k === j ? { ...x, go_to: e.target.value || null } : x)) })}>
                        <option value="">Stays here</option>
                        {body.steps.filter((s) => s.key !== step.key).map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                      </select></td>
                      <td><select className="arc-select" aria-label="Disposition" value={b.disposition ?? ''} disabled={!writer}
                        onChange={(e) => setStep({ buttons: step.buttons.map((x, k) => (k === j ? { ...x, disposition: e.target.value || null } : x)) })}>
                        <option value="">None</option>
                        {active.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select></td>
                      <td className="arc-td-right">{writer && (
                        <RowAction kind="delete" label="Remove button"
                          onClick={() => setStep({ buttons: step.buttons.filter((_, k) => k !== j) })} />
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {writer && step.buttons.length < 8 && (
              <button type="button" className="arc-btn arc-btn-sm" style={{ marginTop: 8 }}
                onClick={() => setStep({ buttons: [...step.buttons, { label: 'Next', go_to: null, disposition: null }] })}>
                <Icon name="plus" size={12} /> Button
              </button>
            )}
          </div>

          <div className="arc-card arc-card-body">
            <SubHead sub="Saved on the lead with the call, and usable in later steps and later calls.">Fields to fill in</SubHead>
            {step.fields.map((f, j) => (
              <div key={j} className="arc-row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', padding: '8px 0', borderTop: j ? '1px solid var(--arc-border)' : undefined }}>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor={`sf-l${j}`}>Label</label>
                  <input id={`sf-l${j}`} className="arc-input" value={f.label} maxLength={80} disabled={!writer} onChange={(e) => setField(j, { label: e.target.value })} />
                </div>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor={`sf-t${j}`}>Type</label>
                  <select id={`sf-t${j}`} className="arc-select" value={f.type} disabled={!writer} onChange={(e) => setField(j, { type: e.target.value as ScriptFieldType })}>
                    {SCRIPT_FIELD_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                  </select>
                </div>
                {f.type === 'choice' && (
                  <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
                    <label className="arc-label" htmlFor={`sf-o${j}`}>Choices, comma separated</label>
                    <input id={`sf-o${j}`} className="arc-input" defaultValue={f.options.join(', ')} disabled={!writer}
                      onBlur={(e) => setField(j, { options: e.target.value.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 20) })} />
                  </div>
                )}
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor={`sf-r${j}`}>Required for</label>
                  <select id={`sf-r${j}`} className="arc-select" multiple size={Math.min(4, Math.max(2, active.length))} disabled={!writer}
                    value={f.required_for} onChange={(e) => setField(j, { required_for: [...e.target.selectedOptions].map((o) => o.value) })}>
                    {active.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <span className="arc-mono arc-subtle" style={{ fontSize: 11.5 }} title="Use in the text">{`{{custom.${f.key}}}`}</span>
                {writer && (
                  <RowAction kind="delete" label="Remove field"
                    onClick={() => setStep({ fields: step.fields.filter((_, k) => k !== j) })} />
                )}
              </div>
            ))}
            {writer && step.fields.length < 20 && (
              <button type="button" className="arc-btn arc-btn-sm" style={{ marginTop: 8 }}
                onClick={() => {
                  const label = `Field ${allFieldKeys.size + 1}`;
                  setStep({ fields: [...step.fields, { key: keyOf(label, allFieldKeys, 'field'), label, type: 'text', options: [], required_for: [] }] });
                }}>
                <Icon name="plus" size={12} /> Field
              </button>
            )}
          </div>

          <Preview tenantId={tenantId} step={step} />
        </div>
      </div>

      {writer && (
        <div className="arc-row" style={{ gap: 10, marginTop: 14 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Saving…' : detail ? 'Save' : 'Make the script'}
          </button>
          {save.isSuccess && <span className="arc-muted" style={{ fontSize: 12.5 }}>Saved</span>}
          {detail && detail.versions.length > 1 && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>
              Version {detail.versions[0]!.version}, {relativeTime(detail.versions[0]!.created_at)}{detail.versions[0]!.by ? ` by ${detail.versions[0]!.by}` : ''}
            </span>
          )}
        </div>
      )}
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}

/** The step as an agent would see it for a real lead. */
function Preview({ tenantId, step }: { tenantId: string; step: ScriptStep }) {
  const lists = useDiallerLists(tenantId);
  const [listId, setListId] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const records = useDiallerRecords(tenantId, listId, {});
  const r = records.data?.data[i] ?? null;
  const agent = useSession((s) => s.session?.user.full_name ?? '');
  const vars: Record<string, string | null> = r ? {
    first_name: r.first_name, last_name: r.last_name, email: r.email, phone: `+${r.phone}`,
    'agent.name': agent || null, 'agent.first_name': agent.split(/\s+/)[0] || null,
    'campaign.name': 'Campaign', caller_id: null, today: new Date().toISOString().slice(0, 10),
    ...Object.fromEntries(Object.entries(r.fields).map(([k, v]) => [`custom.${k}`, v === null ? null : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)])),
  } : {};

  return (
    <div className="arc-card arc-card-body">
      <div className="arc-row" style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
        <h3 className="arc-h2" style={{ margin: 0, flex: 1 }}>Preview</h3>
        <select className="arc-select" aria-label="Preview with a lead from" value={listId ?? ''} style={{ maxWidth: 220 }}
          onChange={(e) => { setListId(e.target.value || null); setI(0); }}>
          <option value="">Choose a list…</option>
          {(lists.data?.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {r && (
          <div className="arc-row" style={{ gap: 4 }}>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Previous lead" disabled={i === 0} onClick={() => setI(i - 1)}><Icon name="chevronLeft" size={12} /></button>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label="Next lead" disabled={i + 1 >= (records.data?.data.length ?? 0)} onClick={() => setI(i + 1)}><Icon name="chevronRight" size={12} /></button>
          </div>
        )}
      </div>
      {listId && records.data && !r && <p className="arc-subtle" style={{ margin: 0 }}>That list has no leads.</p>}
      {r ? <ScriptText text={step.text} vars={vars} /> : !listId && <p className="arc-subtle" style={{ margin: 0 }}>Choose a list to see the step with a real lead’s details.</p>}
    </div>
  );
}
